/**
 * WlPlans — gestão de planos no painel White Label.
 *
 * Espelha funcionalmente /admin/plans (Lovable root), porém:
 *   - Todas as leituras/escritas passam por RPCs SECURITY DEFINER (wl_*)
 *   - Sem ação de excluir (apenas ativar/desativar) — conforme regra do WL
 *   - Botões obedecem useWlPermissions (`wl.plans` create/edit)
 *
 * Layout segue padrão ListPageLayout + KpiCard.
 */
import { useMemo, useState } from "react";
import { Search, Plus, Pencil, Check, X, ToggleLeft, ToggleRight, CreditCard, Infinity as InfinityIcon } from "lucide-react";
import { useWlPermissions } from "@/contexts/WlPermissionsContext";
import { useWlPlans, useWlPlanMutations, type WlPlan } from "@/hooks/useWlPlans";
import { useWlGatewayPaymentMethods } from "@/hooks/useWlGatewayPaymentMethods";
import { showToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  ListPageContent,
} from "@/components/layout/list-page";
import { applyCurrencyMask, parseCurrency } from "@/lib/input-masks";
import {
  WlBillingIntegrationSection,
  type WlBillingIntegrationValue,
} from "@/components/wl/WlBillingIntegrationSection";
import { RecurrenceOptionsEditor } from "@/components/wl/RecurrenceOptionsEditor";
import {
  RecurrenceOptionsInlineEditor,
  type PendingRecurrence,
} from "@/components/wl/RecurrenceOptionsInlineEditor";
import { useWlPlanRecurrenceMutations } from "@/hooks/wl/useWlRecurrenceOptions";


// Tipagem do form (features como string multi-linha)
type PlanForm = {
  id: string;
  name: string;
  description: string;
  max_inboxes: number;
  max_agents: number;
  max_channels: number;
  max_instances: number;
  max_instagram_instances: number;
  max_crm_workspaces: number;
  max_messages_month: number | null;
  max_tasks_month: number;
  max_tasks_storage_mb: number;
  // Novos limites informativos (sem enforcement nesta fase).
  max_pipelines: number;
  max_webhooks: number;
  max_flows: number;
  max_contacts: number;
  price_monthly: number;
  price_yearly: number;
  features: string;
  has_campaigns: boolean;
  // Módulos inclusos automaticamente ao contratar o plano.
  has_crm: boolean;
  has_financial: boolean;
  has_tasks: boolean;
  has_referral: boolean;
  has_digital_contracts: boolean;
  // IDs do Stripe (editáveis no painel WL).
  stripe_product_id: string | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  // Multi-gateway v1.
  billing_integration: "legado" | "integracao_v1";
  billing_gateway_slug: string | null;
  uaizy_master_service_id: string | null;
  // Meios de pagamento aceitos pelo plano — UUIDs dos meios cadastrados no
  // módulo Financeiro do tenant master (resolvidos pelo gateway selecionado).
  accepted_payment_methods: string[];

  // Fase F (Uaizy v1) — trial automático + plano padrão + oculto no self-service.
  trial_days: number;
  is_default_for_new_tenant: boolean;
  is_hidden_from_self_service: boolean;

  // v1.7.0 — modelo de cobrança do plano.
  billing_type: "pre_paid" | "post_paid";

  is_active: boolean;
  sort_order: number;
};


const EMPTY: PlanForm = {
  id: "",
  name: "",
  description: "",
  max_inboxes: 1,
  max_agents: 3,
  max_channels: 1,
  max_instances: 1,
  max_instagram_instances: 0,
  max_crm_workspaces: 1,
  max_messages_month: 1000,
  max_tasks_month: 50,
  max_tasks_storage_mb: 1024,
  max_pipelines: 0,
  max_webhooks: 0,
  max_flows: 0,
  max_contacts: 0,
  price_monthly: 0,
  price_yearly: 0,
  features: "",
  has_campaigns: false,
  has_crm: false,
  has_financial: false,
  has_tasks: false,
  has_referral: false,
  has_digital_contracts: false,
  stripe_product_id: null,
  stripe_price_id_monthly: null,
  stripe_price_id_yearly: null,
  billing_integration: "legado",
  billing_gateway_slug: null,
  uaizy_master_service_id: null,
  accepted_payment_methods: [],

  // Fase F — defaults conservadores.
  trial_days: 0,
  is_default_for_new_tenant: false,
  is_hidden_from_self_service: false,

  // v1.7.0 — default conservador.
  billing_type: "pre_paid",

  is_active: true,
  sort_order: 0,
};


const fromPlan = (p: WlPlan): PlanForm => ({
  ...EMPTY,
  ...p,
  description: p.description ?? "",
  features: (p.features || []).join("\n"),
  stripe_product_id: p.stripe_product_id ?? null,
  stripe_price_id_monthly: p.stripe_price_id_monthly ?? null,
  stripe_price_id_yearly: p.stripe_price_id_yearly ?? null,
  billing_integration: p.billing_integration ?? "legado",
  billing_gateway_slug: p.billing_gateway_slug ?? null,
  uaizy_master_service_id: p.uaizy_master_service_id ?? null,
  accepted_payment_methods: Array.isArray(p.accepted_payment_methods)
    ? p.accepted_payment_methods
    : [],
});

const fmtPrice = (v: number) =>
  v > 0 ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v) : "Grátis";

const fmtLimit = (v: number | null) => {
  if (v === null || v === -1) return <InfinityIcon className="h-3.5 w-3.5 inline" />;
  return v.toLocaleString("pt-BR");
};

export default function WlPlans() {
  const { canCreate: canCreatePerm, canEdit: canEditPerm } = useWlPermissions();
  const canCreate = canCreatePerm("wl.plans");
  const canEdit = canEditPerm("wl.plans");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const includeInactive = statusFilter !== "active";
  const { data: plans = [], isLoading } = useWlPlans({
    includeInactive,
    search: search || null,
  });
  const filtered = useMemo(
    () => (statusFilter === "inactive" ? plans.filter((p) => !p.is_active) : plans),
    [plans, statusFilter],
  );

  const { upsert, toggleActive } = useWlPlanMutations();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WlPlan | null>(null);
  const [form, setForm] = useState<PlanForm>(EMPTY);
  // Periodicidades em memória — usadas apenas no fluxo de CRIAÇÃO de plano V.1,
  // já que o id do plano ainda não existe para persistir por linha.
  const [pendingRecurrences, setPendingRecurrences] = useState<PendingRecurrence[]>([]);
  // Mutations de periodicidade (usadas no submit pós-criação do plano V.1).
  const recurrenceM = useWlPlanRecurrenceMutations(null);

  const totals = useMemo(
    () => ({
      total: plans.length,
      active: plans.filter((p) => p.is_active).length,
      inactive: plans.filter((p) => !p.is_active).length,
    }),
    [plans],
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setPendingRecurrences([]);
    setDialogOpen(true);
  };

  const openEdit = (p: WlPlan) => {
    setEditing(p);
    setForm(fromPlan(p));
    setPendingRecurrences([]);
    setDialogOpen(true);
  };

  // Multi-gateway v1 — flags derivadas para bloquear preço local quando o
  // plano é lastreado em serviço de recorrência do tenant master.
  const isV1 = form.billing_integration === "integracao_v1";
  const isUaizy = isV1 && form.billing_gateway_slug === "uaizy";
  const priceFromMaster = isUaizy;

  // Periodicidades ativas (apenas relevante na CRIAÇÃO V.1).
  const activePending = pendingRecurrences.filter((r) => r.is_active);
  const isCreatingV1 = !editing && isV1;

  const submit = async () => {
    if (!form.name || (!editing && !form.id)) {
      showToast.error("Preencha ID e Nome");
      return;
    }
    // Validações multi-gateway alinhadas com triggers do backend.
    if (isV1 && !form.billing_gateway_slug) {
      showToast.error("Selecione o gateway de cobrança");
      return;
    }
    // Pelo menos um meio de pagamento aceito (alinhado com CHECK do banco).
    if (!form.accepted_payment_methods || form.accepted_payment_methods.length === 0) {
      showToast.error("Selecione ao menos um meio de pagamento aceito");
      return;
    }
    // Validação pré-submit das periodicidades (somente criação V.1).
    if (isCreatingV1) {
      if (activePending.length === 0) {
        showToast.error("Configure pelo menos uma periodicidade ativa");
        return;
      }
      const invalid = activePending.find(
        (r) => !r.uaizy_master_service_id || !(r.price > 0),
      );
      if (invalid) {
        showToast.error(
          `Periodicidade ${invalid.recurrence_interval}: selecione o serviço e informe o preço`,
        );
        return;
      }
    }
    // OBS: a vinculação do serviço master agora é por periodicidade
    // (plan_recurrence_options), não mais no nível do plano.
    try {
      const planId = await upsert.mutateAsync({
        id: editing?.id ?? null,
        payload: {
          ...form,
          // Vínculo do serviço master agora é por periodicidade
          // (plan_recurrence_options). No nível do plano, sempre enviar null
          // quando for V.1 — evita disparar a validação legada do trigger.
          uaizy_master_service_id: isV1 ? null : form.uaizy_master_service_id,
          features: form.features
            .split("\n")
            .map((f) => f.trim())
            .filter(Boolean),
        },
      });


      // Pós-criação V.1: persistir cada periodicidade ativa sequencialmente
      // via RPC já existente. Erros aqui propagam para o catch externo, mas o
      // plano permanece criado — o usuário pode reabrir em modo edição.
      if (isCreatingV1 && planId) {
        for (const r of activePending) {
          await recurrenceM.upsert.mutateAsync({
            id: null,
            payload: {
              plan_id: planId,
              recurrence_interval: r.recurrence_interval,
              uaizy_master_service_id: r.uaizy_master_service_id,
              price: r.price,
              setup_fee: r.setup_fee,
              discount_percent: r.discount_percent,
              is_active: true,
            },
          });
        }
      }

      showToast.success(editing ? "Plano atualizado" : "Plano criado");
      setDialogOpen(false);
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao salvar plano");
    }
  };

  const handleToggle = async (p: WlPlan) => {
    if (p.is_active && p.tenants_using_count > 0) {
      const ok = confirm(
        `Atenção: existem ${p.tenants_using_count} tenant(s) usando este plano. Desativar mesmo assim?`,
      );
      if (!ok) return;
    }
    try {
      await toggleActive.mutateAsync({ id: p.id, active: !p.is_active });
      showToast.success(`Plano ${!p.is_active ? "ativado" : "desativado"}`);
    } catch (e: any) {
      showToast.error(e?.message || "Falha ao alterar status");
    }
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Planos"
        description="Cadastro de planos de assinatura"
        actions={
          canCreate ? (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> Novo Plano
            </Button>
          ) : null
        }
      />

      <ListPageKpis columns={3}>
        <KpiCard color="purple" icon={CreditCard} label="Total" value={isLoading ? "…" : totals.total} />
        <KpiCard
          color="green"
          icon={Check}
          label="Ativos"
          value={isLoading ? "…" : totals.active}
          onClick={() => setStatusFilter(statusFilter === "active" ? "all" : "active")}
          active={statusFilter === "active"}
        />
        <KpiCard
          color="red"
          icon={X}
          label="Inativos"
          value={isLoading ? "…" : totals.inactive}
          onClick={() => setStatusFilter(statusFilter === "inactive" ? "all" : "inactive")}
          active={statusFilter === "inactive"}
        />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={isLoading}>
        <div className="relative flex-1 min-w-[220px] sm:flex-none sm:w-[280px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Buscar por ID ou nome…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)}
            onBlur={() => searchInput !== search && setSearch(searchInput)}
          />
        </div>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : filtered.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground">Nenhum plano encontrado</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium">Plano</TableHead>
                <TableHead className="text-xs font-medium">Descrição</TableHead>
                <TableHead className="text-xs font-medium text-center">Modo</TableHead>
                <TableHead className="text-xs font-medium text-center">Inboxes</TableHead>
                <TableHead className="text-xs font-medium text-center">Agentes</TableHead>
                <TableHead className="text-xs font-medium text-center">Canais</TableHead>
                <TableHead className="text-xs font-medium text-center">Msgs/Mês</TableHead>
                <TableHead className="text-xs font-medium text-right">Preço Mensal</TableHead>
                <TableHead className="text-xs font-medium text-center">Tenants</TableHead>
                <TableHead className="text-xs font-medium text-center">Status</TableHead>
                <TableHead className="text-xs font-medium text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="py-2 text-xs">
                    <div className="font-medium flex items-center gap-1.5 flex-wrap">
                      <span>{p.name}</span>
                      {p.is_default_for_new_tenant && (
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-amber-400/60 text-amber-600 px-1.5 py-0">
                          Default
                        </Badge>
                      )}
                      {p.trial_days > 0 && (
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-emerald-400/60 text-emerald-600 px-1.5 py-0">
                          Trial {p.trial_days}d
                        </Badge>
                      )}
                      {p.is_hidden_from_self_service && (
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-zinc-400/60 text-zinc-500 px-1.5 py-0">
                          Oculto
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{p.id}</div>
                  </TableCell>

                  <TableCell className="py-2 text-xs text-muted-foreground max-w-[220px] truncate">
                    {p.description || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-center">
                    {p.billing_integration === "integracao_v1" ? (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-primary/40 text-primary">
                        V.1{p.billing_gateway_slug ? ` · ${p.billing_gateway_slug}` : ""}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                        Legado
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-center">{fmtLimit(p.max_inboxes)}</TableCell>
                  <TableCell className="py-2 text-xs text-center">{fmtLimit(p.max_agents)}</TableCell>
                  <TableCell className="py-2 text-xs text-center">{fmtLimit(p.max_channels)}</TableCell>
                  <TableCell className="py-2 text-xs text-center">{fmtLimit(p.max_messages_month)}</TableCell>
                  <TableCell className="py-2 text-xs text-right">{fmtPrice(p.price_monthly)}</TableCell>
                  <TableCell className="py-2 text-xs text-center">{p.tenants_using_count}</TableCell>
                  <TableCell className="py-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
                      <span
                        className={cn("h-2 w-2 rounded-full shrink-0", p.is_active ? "bg-green-500" : "bg-zinc-400")}
                      />
                      {p.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!canEdit}
                        onClick={() => openEdit(p)}
                        title="Editar"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={!canEdit || toggleActive.isPending}
                        onClick={() => handleToggle(p)}
                        title={p.is_active ? "Desativar" : "Ativar"}
                      >
                        {p.is_active ? (
                          <ToggleRight className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListPageContent>

      {/* ----- Dialog Criar/Editar Plano ----- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Plano" : "Novo Plano"}</DialogTitle>
            <DialogDescription>
              {editing ? "Atualize as informações do plano" : "Configure um novo plano de assinatura"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2">
              <Label>ID do Plano</Label>
              <Input
                placeholder="ex: custom_plan"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                disabled={!!editing}
                maxLength={40}
              />
            </div>
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                placeholder="Nome do plano"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                maxLength={80}
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Descrição</Label>
              <Input
                placeholder="Descrição breve"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                maxLength={200}
              />
            </div>

            <div className="col-span-2">
              <h4 className="font-medium mb-2 text-sm">Limites (-1 = ilimitado)</h4>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Inboxes</Label>
                  <Input type="number" value={form.max_inboxes}
                    onChange={(e) => setForm({ ...form, max_inboxes: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Agentes</Label>
                  <Input type="number" value={form.max_agents}
                    onChange={(e) => setForm({ ...form, max_agents: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Canais</Label>
                  <Input type="number" value={form.max_channels}
                    onChange={(e) => setForm({ ...form, max_channels: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Msgs/Mês</Label>
                  <Input type="number" value={form.max_messages_month ?? ""}
                    placeholder="∞"
                    onChange={(e) =>
                      setForm({ ...form, max_messages_month: e.target.value ? parseInt(e.target.value) : null })
                    } />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Instâncias</Label>
                  <Input type="number" value={form.max_instances}
                    onChange={(e) => setForm({ ...form, max_instances: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Instagram</Label>
                  <Input type="number" value={form.max_instagram_instances}
                    onChange={(e) => setForm({ ...form, max_instagram_instances: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Workspaces CRM</Label>
                  <Input type="number" value={form.max_crm_workspaces}
                    onChange={(e) => setForm({ ...form, max_crm_workspaces: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tarefas/Mês</Label>
                  <Input type="number" value={form.max_tasks_month}
                    onChange={(e) => setForm({ ...form, max_tasks_month: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Storage Tarefas (MB)</Label>
                  <Input type="number" value={form.max_tasks_storage_mb}
                    onChange={(e) => setForm({ ...form, max_tasks_storage_mb: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Pipelines</Label>
                  <Input type="number" value={form.max_pipelines}
                    onChange={(e) => setForm({ ...form, max_pipelines: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Webhooks</Label>
                  <Input type="number" value={form.max_webhooks}
                    onChange={(e) => setForm({ ...form, max_webhooks: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Automações/Fluxos</Label>
                  <Input type="number" value={form.max_flows}
                    onChange={(e) => setForm({ ...form, max_flows: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Contatos</Label>
                  <Input type="number" value={form.max_contacts}
                    onChange={(e) => setForm({ ...form, max_contacts: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
            </div>

            {/* ============= Meios de pagamento aceitos pelo plano =============
                Listagem dinâmica: depende do gateway selecionado. Hoje só o
                gateway `uaizy` devolve meios (cadastrados no Financeiro do
                tenant master). */}
            <div className="col-span-2">
              <h4 className="font-medium mb-2 text-sm">Meios de pagamento aceitos</h4>
              {!form.billing_gateway_slug ? (
                <p className="text-xs text-muted-foreground">
                  Selecione um gateway de cobrança para listar os meios de pagamento disponíveis.
                </p>
              ) : (
                <GatewayPaymentMethodsPicker
                  gatewaySlug={form.billing_gateway_slug}
                  selected={form.accepted_payment_methods}
                  onChange={(next) =>
                    setForm({ ...form, accepted_payment_methods: next })
                  }
                />
              )}
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Selecione pelo menos um. Os botões aparecerão/ocultarão na página de planos do tenant conforme esta seleção.
              </p>
            </div>



            <div className="col-span-2">
              {/* Bloco Integração (multi-gateway v1). */}
              <WlBillingIntegrationSection
                value={{
                  billing_integration: form.billing_integration,
                  billing_gateway_slug: form.billing_gateway_slug,
                  uaizy_master_service_id: form.uaizy_master_service_id,
                }}
                onChange={(next: WlBillingIntegrationValue) => {
                  // Ao mudar o gateway, limpamos os meios de pagamento aceitos
                  // (eles são vinculados ao gateway). O operador precisa
                  // reselecionar de acordo com o novo gateway.
                  const gatewayChanged =
                    next.billing_gateway_slug !== form.billing_gateway_slug;
                  setForm({
                    ...form,
                    ...next,
                    accepted_payment_methods: gatewayChanged
                      ? []
                      : form.accepted_payment_methods,
                  });
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Preço Mensal</Label>
              <Input
                placeholder="R$ 0,00"
                value={form.price_monthly > 0 ? applyCurrencyMask(form.price_monthly) : ""}
                onChange={(e) => setForm({ ...form, price_monthly: parseCurrency(e.target.value) })}
                disabled={priceFromMaster}
              />
              {priceFromMaster && (
                <p className="text-[11px] text-muted-foreground">
                  Preço vem do serviço do tenant master.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Preço Anual</Label>
              <Input
                placeholder="R$ 0,00"
                value={form.price_yearly > 0 ? applyCurrencyMask(form.price_yearly) : ""}
                onChange={(e) => setForm({ ...form, price_yearly: parseCurrency(e.target.value) })}
                disabled={priceFromMaster}
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label>Features (uma por linha)</Label>
              <Textarea
                rows={3}
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                maxLength={2000}
              />
            </div>

            {/* Integração Stripe — IDs do produto/preço para conectar ao integrador financeiro.
                Oculto quando billing_integration = integracao_v1 (gateway uaizy), pois não usa Stripe. */}
            {!isV1 && (
              <div className="col-span-2 rounded-md border p-3 space-y-3">
                <h4 className="font-medium text-sm">Integração Stripe</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="stripe_product">Product ID</Label>
                    <Input
                      id="stripe_product"
                      placeholder="prod_..."
                      value={form.stripe_product_id || ""}
                      onChange={(e) => setForm({ ...form, stripe_product_id: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="stripe_price_monthly">Price Mensal ID</Label>
                    <Input
                      id="stripe_price_monthly"
                      placeholder="price_..."
                      value={form.stripe_price_id_monthly || ""}
                      onChange={(e) => setForm({ ...form, stripe_price_id_monthly: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="stripe_price_yearly">Price Anual ID</Label>
                    <Input
                      id="stripe_price_yearly"
                      placeholder="price_..."
                      value={form.stripe_price_id_yearly || ""}
                      onChange={(e) => setForm({ ...form, stripe_price_id_yearly: e.target.value || null })}
                    />
                  </div>
                </div>
              </div>
            )}


            <div className="space-y-2">
              <Label>Ordem</Label>
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Plano Ativo</Label>
            </div>

            {/* ============ Fase F — Trial automático + plano padrão ============ */}
            <div className="col-span-2 space-y-3 rounded-lg border p-4">
              <div>
                <Label className="text-sm font-semibold">Trial automático & plano padrão</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Configure o período de teste e marque este plano como padrão para novos tenants.
                  Planos padrão geralmente ficam ocultos na página de planos do cliente.
                </p>
              </div>

              {/* v1.7.0 — Modelo de cobrança (pre-paid x post-paid) */}
              <div className="space-y-2 border-b pb-3">
                <Label className="text-xs font-semibold">Modelo de cobrança</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, billing_type: "pre_paid" })}
                    className={cn(
                      "text-left rounded-md border p-3 text-xs transition",
                      form.billing_type === "pre_paid"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50",
                    )}
                  >
                    <div className="font-semibold mb-1">Pré-pago</div>
                    <div className="text-muted-foreground">
                      Cliente paga primeiro. Funcionalidades só liberadas após confirmação
                      do pagamento (webhook). Mudanças de plano ficam pendentes até quitação.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, billing_type: "post_paid" })}
                    className={cn(
                      "text-left rounded-md border p-3 text-xs transition",
                      form.billing_type === "post_paid"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50",
                    )}
                  >
                    <div className="font-semibold mb-1">Pós-pago</div>
                    <div className="text-muted-foreground">
                      Libera imediatamente, cobra no fechamento do ciclo. Mudanças de plano
                      e add-ons entram em vigor na hora; cobranças já emitidas permanecem
                      com o valor antigo.
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs" htmlFor="trial_days">Período de teste (dias)</Label>
                  <Input
                    id="trial_days"
                    type="number"
                    min={0}
                    max={365}
                    value={form.trial_days}
                    onChange={(e) =>
                      setForm({ ...form, trial_days: Math.max(0, parseInt(e.target.value) || 0) })
                    }
                    placeholder="0 = sem trial"
                  />
                  <p className="text-[11px] text-muted-foreground">0 = sem trial.</p>
                </div>
                <div className="flex items-start gap-2 pt-5">
                  <Switch
                    id="is_default_for_new_tenant"
                    checked={form.is_default_for_new_tenant}
                    onCheckedChange={(v) =>
                      setForm({ ...form, is_default_for_new_tenant: v })
                    }
                  />
                  <div>
                    <Label htmlFor="is_default_for_new_tenant" className="font-normal">
                      Plano padrão de novos tenants
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Único por gateway. Provisionado em trial no cadastro.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 pt-5">
                  <Switch
                    id="is_hidden_from_self_service"
                    checked={form.is_hidden_from_self_service}
                    onCheckedChange={(v) =>
                      setForm({ ...form, is_hidden_from_self_service: v })
                    }
                  />
                  <div>
                    <Label htmlFor="is_hidden_from_self_service" className="font-normal">
                      Ocultar da página de planos
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Recomendado para planos default (cliente não precisa escolher).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção: módulos inclusos automaticamente ao contratar este plano */}
            <div className="col-span-2 space-y-3 rounded-lg border p-4">
              <div>
                <Label className="text-sm font-semibold">Módulos inclusos no plano</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Os módulos marcados serão habilitados automaticamente para o tenant ao contratar este plano.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_campaigns}
                    onCheckedChange={(v) => setForm({ ...form, has_campaigns: v })} />
                  <Label className="font-normal">Módulo Campanhas</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_crm}
                    onCheckedChange={(v) => setForm({ ...form, has_crm: v })} />
                  <Label className="font-normal">Módulo CRM</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_financial}
                    onCheckedChange={(v) => setForm({ ...form, has_financial: v })} />
                  <Label className="font-normal">Módulo Financeiro</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_tasks}
                    onCheckedChange={(v) => setForm({ ...form, has_tasks: v })} />
                  <Label className="font-normal">Módulo Tarefas</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_referral}
                    onCheckedChange={(v) => setForm({ ...form, has_referral: v })} />
                  <Label className="font-normal">Módulo Indicação</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.has_digital_contracts}
                    onCheckedChange={(v) => setForm({ ...form, has_digital_contracts: v })} />
                  <Label className="font-normal">Módulo Contrato Digital</Label>
                </div>
              </div>
            </div>

            {/* Periodicidades múltiplas — apenas para planos V.1.
                - Edição: editor persistido (salva por linha via RPC).
                - Criação: editor in-memory; persistido em sequência após criar
                  o plano em submit() reaproveitando wl_upsert_plan_recurrence_option. */}
            {editing && isV1 && (
              <div className="col-span-2">
                <RecurrenceOptionsEditor entity="plan" entityId={editing.id} />
              </div>
            )}
            {!editing && isV1 && (
              <div className="col-span-2">
                <RecurrenceOptionsInlineEditor
                  value={pendingRecurrences}
                  onChange={setPendingRecurrences}
                />
              </div>
            )}
          </div>




          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={upsert.isPending}>
              {upsert.isPending ? "Salvando…" : editing ? "Salvar" : "Criar Plano"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}

/* ============================================================================
 * GatewayPaymentMethodsPicker
 * ----------------------------------------------------------------------------
 * Pequeno componente local que consome `useWlGatewayPaymentMethods` para o
 * gateway selecionado e renderiza um botão toggle por meio cadastrado no
 * Financeiro do tenant master.
 * ========================================================================== */
function GatewayPaymentMethodsPicker({
  gatewaySlug,
  selected,
  onChange,
}: {
  gatewaySlug: string;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const { data: methods = [], isLoading, error } = useWlGatewayPaymentMethods(gatewaySlug);

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Carregando meios de pagamento…</div>;
  }
  if (error) {
    return (
      <div className="text-xs text-rose-600">
        Falha ao carregar meios de pagamento: {(error as Error).message}
      </div>
    );
  }
  if (!methods.length) {
    return (
      <div className="text-xs text-muted-foreground">
        Nenhum meio de pagamento cadastrado no tenant master para este gateway.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {methods.map((m) => {
        const active = selected.includes(m.id);
        return (
          <button
            type="button"
            key={m.id}
            onClick={() => {
              const next = active
                ? selected.filter((x) => x !== m.id)
                : [...selected, m.id];
              onChange(next);
            }}
            className={
              "px-3 py-1.5 rounded-md border text-xs transition-colors " +
              (active
                ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                : "border-border text-muted-foreground hover:border-emerald-500/50")
            }
            title={m.tipo_pagamento}
          >
            {m.nome}
          </button>
        );
      })}
    </div>
  );
}
